import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Steps } from "primereact/steps";
import { MenuItem } from "primereact/menuitem";
import { Property } from "../../../interface/tracker";
import { AddPropertyContext } from "./AddPropertyContext";
import Summary from "./Summary";
import PurchaseAndLoans from "./PurchaseAndLoans";
import RentAndExpenses from "./RentAndExpenses";
import TaxAndMarket from "./TaxAndMarket";
import Review from "./Review";
import Results from "./Results";
import { REIButton, REICheckBox, REIDialoge } from "../../../components/ui";
import { IUserProfileDetails } from "../../../interface/userdetails";
import { useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { DATE_FORMAT, MEMBER_KEY, USER_DETAIL } from "../../../utils/constants";
import { formatDayCycle, getAdjustedFormattedDate, getDateStringWithGivenFormat } from "../../../utils/commonUtil";
import MyDefaultProfile from "../../Search/Child/defaultProfile";

interface AddPropertyProps {
  onClose: (portfolioTrackerId?: number) => void;
}

const AddProperty = (props: AddPropertyProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [useDefault, setUseDefault] = useState(false);
  const [userProfile, setUserProfile] = useState<IUserProfileDetails | undefined>(undefined);
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberName = `${memberDetail.FirstName} ${memberDetail.LastName}`;
  const [showDefaultProfile, setShowDefaultProfile] = useState<boolean>(false);
  const [reloadProfile, setReloadProfile] = useState<boolean>(false);
  const [portfolioTrackerId, setPortfolioTrackerId] = useState<number | undefined>(undefined);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const condition = useDefault
        ? `?ModulesId=${localStorage.getItem(MEMBER_KEY)}&Country=${memberDetail.Country}&memberName=${memberName}`
        : `/${memberDetail.Country}`;

      try {
        const result = await useHttpGets(`${ApiEndPoints.analyser}/UserDefault${condition}`);
        if (result.status === 200 && result.data) {
          setUserProfile(result.data as IUserProfileDetails);
        }
      } catch {
        console.error("Error fetching user profile.");
      }
    };

    fetchUserProfile();
  }, [useDefault, reloadProfile]);

  const initialPropertyData = {
    MemberId: localStorage.getItem("memberId") || "",
    MemberEntityId: undefined,
    PortionOwned: 100,
    CountryCode: "1",
    StreetAddress: "",
    Suburb: "",
    District: "",
    StateRegion: "",
    PostCode: "",
    Name: "",
    PropertyTypeId: 1,
    BedroomCode: "2",
    BathroomCode: "2",
    ParkingCode: "2",
    LandSize_sqm: undefined,
    FloorSize_sqm: undefined,
    DepositCash: 10,
    PurchasePrice: 0,
    EstimatedLandValueDate: getDateStringWithGivenFormat(new Date(Date.now()), DATE_FORMAT.DDMMYYYY),
    PurchaseDate: "",
    PurchaseType: "new",
    AmountOwning: 0,
    Current_MarketValue: 0,
    Current_InitialPurchasePrice: 0,
    EstimatedMarketValue: 0,
    EstimatedMarketValueDate: getDateStringWithGivenFormat(new Date(Date.now()), DATE_FORMAT.DDMMYYYY),
    EstimatedLandValue: 0,
    Cost_CapitalImprovements: 0,
    Cost_OtherCosts: 0,
    Cost_SolicitorFees: 1000,
    Rental_IsResidential: 1,
    Rental_NumWeeks: 52,
    Rental_OccupancyRate: 52,
    Rental_WeeklyRent: 0,
    Rental_Peak_NumWeeks: 0,
    Rental_Peak_OccupancyRate: 0,
    Rental_Peak_WeeklyRent: 0,
    Rental_Shoulder_NumWeeks: 0,
    Rental_Shoulder_OccupancyRate: 0,
    Rental_Shoulder_WeeklyRent: 0,
    Rental_OffPeak_NumWeeks: 0,
    Rental_OffPeak_OccupancyRate: 0,
    Rental_OffPeak_WeeklyRent: 0,
    Expense_Rates: 0,
    Expense_Insurance: 0,
    Expense_LandLease: 0,
    Expense_LandTax: 0,
    Expense_RepairsMaintenance: 0,
    Expense_ServiceContracts: 500,
    Expense_GardeningFees: 0,
    Expense_LettingFees: 0,
    Expense_BodyCorporateFees: 0,
    Expense_CleaningFees: 0,
    Expense_OtherExpenses: 0,
    Expense_AgentsRentalCommissionRate: 8,
    Lease_Tenants: "",
    Lease_StartDate: "",
    Lease_EndDate: "",
    Lease_ReviewAlert: 0,
    Lease_ReviewDate: "",
    ConsumerPriceIndexRate: 3,
    CapitalGrowthRate: 6,
    ConstructionYear: 1974,
    Property_PurchastType: "percentage",
    //ConstructionDate: "01/01/1974",
    VIRenovationTypeCode: "3",
    WBQualityTypeCode: "2",
    WBNearestCityId: 3,
    WBPropertyTypeId: "1",
    LoanToValueRatioTarget: 0.8,
    Year1ProRata: 1,
    Year1ProRataDateExpense: getDateStringWithGivenFormat(new Date(Date.now()), DATE_FORMAT.DDMMYYYY),
    Year1ProRataDateIncome: null,
    Depreciation_ApplyFrom: "",
    userCountryCode: memberDetail.Country,
    PortfolioTrackerId: undefined,
    MemberPropertyId: "736746",
    CalculateDepreciation: "1",
    XeroSync: 0,
    XeroSyncInProgress: 0,
    XeroSyncLastRunUTC: "",
    Loans: [
      {
        TrackerLoanId: 0,
        Name: `${formatDayCycle(1)} loan`,
        Length_Years: userProfile?.MortgageType.LengthOfLoan.toString() || "31",
        Length_Months: userProfile?.MortgageType.FixedPeriodMonhts.toString() || "1",
        EstablishmentFees: userProfile?.LoanCost.EstPrice.toString() || "500",
        InterestRate: userProfile?.MortgageType.InterestRate.toString() || "5",
        FixedInterestPeriod_Years: userProfile?.MortgageType.FixedPeriodYears.toString() || "6",
        FixedInterestPeriod_Months: userProfile?.MortgageType.FixedPeriodMonhts.toString() || "1",
        OtherLoanCost: userProfile?.LoanCost.OtherLoanCost.toString() || "0",
        OtherLoanCosts: userProfile?.LoanCost.OtherLoanCost.toString() || "0",
        ValuationFees: userProfile?.LoanCost.ValuationFee.toString() || "500",
        InterestRateReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 5 }),
        InterestRateReviewAlert: "1",
        Date: getDateStringWithGivenFormat(new Date(Date.now()), DATE_FORMAT.MMDDYYYY),
        RefCode: "",
        isNewLoan: 1,
        Balance: 0,
        IsPrincipal: 0,
      },
    ],
    ExistingLoans: [],
    ValuationEstimateDate: "",
    ValuationEstimatePrice: 0,
    ValuationEstimatePriceRangeMin: 0,
    ValuationEstimatePriceRangeMax: 0,
    ValuationEstimateTrafficLight: "",
    ValuationEstimateConfidenceScore: "",
    PropertyGrowthStatSet: {
      PropertyType: "House",
      StatsPostcode: "4000",
      StatsPostcodeLongTermGrowth: 0.051969605,
      StatsRegion: "Brisbane North",
      StatsRegionLongTermGrowth: 0.058108213,
      StatsSuburb: "Brisbane City",
      StatsSuburbLongTermGrowth: 0.028380705,
    },
    PurchaseCost_TotalCapitalImprovements: 103000,
    PurchaseCost_TotalStampDuty: 0,
    PurchaseCost_TotalSolicitorFees: 1000,
    PurchaseCost_TotalOtherCosts: 0,
  };

  const [property, setProperty] = useState<Partial<Property>>({
    ...initialPropertyData,
    CountryCode: parseInt(initialPropertyData.CountryCode, 10),
    BedroomCode: parseInt(initialPropertyData.BedroomCode, 10),
    BathroomCode: parseInt(initialPropertyData.BathroomCode, 10),
    ParkingCode: parseInt(initialPropertyData.ParkingCode, 10),
    VIRenovationTypeCode: parseInt(initialPropertyData.VIRenovationTypeCode, 10),
    WBQualityTypeCode: parseInt(initialPropertyData.WBQualityTypeCode, 10),
    WBPropertyTypeId: parseInt(initialPropertyData.WBPropertyTypeId, 10),
    PortfolioTrackerId: undefined,
    ExistingLoans: initialPropertyData.ExistingLoans.map((loan) => ({
      ...loan,
      Balance: parseFloat(loan.Balance),
      Length_Years: parseInt(loan.Length_Years, 10),
      Length_Months: parseInt(loan.Length_Months, 10),
      InterestRateReviewAlert: parseInt(loan.InterestRateReviewAlert, 10),
      InterestRate: parseFloat(loan.InterestRate),
      FixedInterestPeriod_Years: parseInt(loan.FixedInterestPeriod_Years, 10),
      FixedInterestPeriod_Months: parseInt(loan.FixedInterestPeriod_Months, 10),
      IsPrincipal: 0,
      isNewLoan: loan.isNewLoan,
    })),
  });

  useEffect(() => {
    if (userProfile && !props.portfolioTrackerId && property.Loans && useDefault) {
      const modifiedFields = property.PurchaseModifiedFields;
      const newProperty = {
        ...property,
        ...(property.PurchaseModifiedFields || {}),
        Property_PurchaseType: userProfile.LoanDeposit.EnterPercentage == "1" ? "percentage" : "value",
        DepositCash:
          userProfile.LoanDeposit.EnterPercentage == "1"
            ? userProfile.LoanDeposit.CashDepositPercentage
            : userProfile.LoanDeposit.CashDeposit,
        Cost_SolicitorFees: (modifiedFields ?? {})["Cost_SolicitorFees"]
          ? property.Cost_SolicitorFees
          : userProfile.LoanCost.SolicitorFee,
        Cost_CapitalImprovements: (modifiedFields ?? {})["Cost_CapitalImprovements"]
          ? property.Cost_CapitalImprovements
          : userProfile.LoanCost.ImprovementFee,
        Cost_OtherCosts: (modifiedFields ?? {})["Cost_OtherCosts"] ? property.Cost_OtherCosts : 0,
        ConsumerPriceIndexRate: userProfile?.TaxRateMarketFactors.ConsumerPrice || 0,
        CapitalGrowthRate: userProfile?.TaxRateMarketFactors.CapitalGrowth || 0,
        Loans: property.Loans.map((loan) => ({
          ...loan,
          Length_Years: userProfile?.MortgageType.LengthOfLoan.toString() || "31",
          Length_Months: "1",
          EstablishmentFees: userProfile?.LoanCost.EstPrice.toString() || "500",
          InterestRate: userProfile?.MortgageType.InterestRate.toString() || "5",
          FixedInterestPeriod_Years: userProfile?.MortgageType.FixedPeriodYears.toString() || "6",
          FixedInterestPeriod_Months: userProfile?.MortgageType.FixedPeriodMonhts.toString() || "1",
          OtherLoanCost: userProfile?.LoanCost.OtherLoanCost.toString() || "0",
          OtherLoanCosts: userProfile?.LoanCost.OtherLoanCost.toString() || "0",
          ValuationFees: userProfile?.LoanCost.ValuationFee.toString() || "500",
          Name: `${formatDayCycle(1)} loan`,
          RefCode: "",
          InterestRateReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 5 }),
          IsPrincipal: userProfile.MortgageType.PrincipalNInterest == "1" ? 1 : 0,
          Date: getDateStringWithGivenFormat(new Date(Date.now()), DATE_FORMAT.MMDDYYYY),
          isNewLoan: 1,
          Balance: 0,
          InterestRateReviewAlert: 1,
        })),
        Expense_Rates:
          userProfile?.PropertyExpenseStandard.isRatePercentage == "1"
            ? parseInt(
                (
                  (property?.PurchasePrice ?? 0) *
                  (userProfile.PropertyExpenseStandard.RatesPercentage / 100)
                ).toString(),
                10
              )
            : userProfile.PropertyExpenseStandard.Rates,
        Expense_Insurance:
          userProfile?.PropertyExpenseStandard.isInsurancePercentage == "1"
            ? parseInt(
                (
                  (property?.PurchasePrice ?? 0) *
                  (userProfile.PropertyExpenseStandard.InsurancePercentage / 100)
                ).toString(),
                10
              )
            : userProfile.PropertyExpenseStandard.Insurance,
        Expense_RepairsMaintenance:
          userProfile?.PropertyExpenseStandard.isRepairPercentage == "1"
            ? parseInt(
                (
                  (property?.PurchasePrice ?? 0) *
                  (userProfile.PropertyExpenseStandard.RepairPercentage / 100)
                ).toString(),
                10
              )
            : userProfile.PropertyExpenseStandard.Repair,
        Expense_BodyCorporateFees:
          userProfile?.PropertyExpenseSpecific.isBodyCorporatePercentage == "1"
            ? parseInt(
                (
                  (property?.PurchasePrice ?? 0) *
                  (userProfile.PropertyExpenseSpecific.BodyCorpatePercentage / 100)
                ).toString(),
                10
              )
            : userProfile.PropertyExpenseSpecific.BodyCorporate,
        Expense_LandLease: userProfile?.PropertyExpenseSpecific.LandLease,
        Expense_LettingFees: userProfile?.PropertyExpenseStandard.LettingFee,
        Expense_AgentsRentalCommissionRate: userProfile?.PropertyExpenseStandard.AgentComm,
        Expense_GardeningFees: userProfile?.PropertyExpenseSpecific.Gardening,
        Expense_CleaningFees: userProfile?.PropertyExpenseStandard.Cleaning,
        Expense_ServiceContracts: userProfile?.PropertyExpenseStandard.ServiceCont,
        Expense_OtherExpenses: userProfile?.PropertyExpenseStandard.Others,
      };
      if (newProperty.ExistingLoans) {
        newProperty.ExistingLoans = newProperty.ExistingLoans.map((loan) => ({
          ...loan,
          Length_Years: Number(loan.Length_Years),
          Length_Months: Number(loan.Length_Months),
          EstablishmentFees: Number(loan.EstablishmentFees),
          InterestRate: Number(loan.InterestRate),
          FixedInterestPeriod_Years: Number(loan.FixedInterestPeriod_Years),
          FixedInterestPeriod_Months: Number(loan.FixedInterestPeriod_Months),
          OtherLoanCost: Number(loan.OtherLoanCost),
          ValuationFees: Number(loan.ValuationFees),
          Balance: 0,
          InterestRateReviewAlert: 1,
          IsPrincipal: 1,
          Date: "",
          isNewLoan: 0,
        }));
      }

      setProperty(newProperty);
    }
  }, [userProfile]);

  const hideDefaultProfile = useCallback(async () => {
    setShowDefaultProfile(false);
    setReloadProfile((prev) => !prev);
  }, []);

  const items: MenuItem[] = [
    { label: "Summary" },
    { label: "Purchase & loans" },
    { label: "Rent & expenses" },
    { label: "Tax & market" },
    { label: "Review" },
    { label: "Results" },
  ];

  const handleNext = () => {
    if (activeIndex === 3) {
      const idStr = localStorage.getItem("portfolioTrackerId");
      setPortfolioTrackerId(idStr ? Number(idStr) : undefined);
      localStorage.removeItem("portfolioTrackerId");
    }
    setActiveIndex((prevActiveIndex) => prevActiveIndex + 1);
  };

  const closeAddProperty = () => {
    props.onClose(portfolioTrackerId ? portfolioTrackerId : 0);
  };

  const handleBack = () => {
    setActiveIndex((prevActiveIndex) => prevActiveIndex - 1);
  };

  const renderStepContent = () => {
    switch (activeIndex) {
      case 0:
        return <Summary onNext={handleNext} />;
      case 1:
        return <PurchaseAndLoans onNext={handleNext} onBack={handleBack} isUserDefault={useDefault} />;
      case 2:
        return <RentAndExpenses onNext={handleNext} onBack={handleBack} />;
      case 3:
        return <TaxAndMarket onNext={handleNext} onBack={handleBack} portfolioTrackerId={portfolioTrackerId} />;
      case 4:
        return <Review onNext={closeAddProperty} onBack={handleBack} portfolioTrackerId={portfolioTrackerId} />;
      case 5:
        return <Results onBack={handleBack} />;
      default:
        return <div>Step {activeIndex + 1}</div>;
    }
  };

  return (
    <AddPropertyContext.Provider value={{ property, setProperty, userProfile, setUserProfile }}>
      <div className="p-card p-5">
        <REIDialoge
          showHeader={true}
          header={`Edit Default Profile`}
          visible={showDefaultProfile}
          handleEditClick={hideDefaultProfile}
          position="top"
          style={{ width: "60vw" }}
          customClass="edit-default-profile"
          contentComponent={<MyDefaultProfile closeDialog={hideDefaultProfile} />}
        ></REIDialoge>
        <div className="flex justify-content-between">
          <h2 className="text-2xl font-bold">Portfolio Tracker – Add a Property</h2>
        </div>

        {!portfolioTrackerId && activeIndex < 1 && (
          <div className="flex w-full text-center content-center">
            <REICheckBox
              id={"apidx-chk-usedefault"}
              className="mr-1"
              label="Use my default profile for this property"
              checked={useDefault}
              onChange={(e) => {
                setUseDefault(e.checked);
              }}
            />
            <REIButton
              id={"apidx-btn-viewdefault"}
              label="(view default profile)"
              classNames="p-button-link mt-[-10px] ml-[-10px]"
              onClick={() => {
                setShowDefaultProfile(true);
              }}
            />
          </div>
        )}

        <div className="p-grid ">
          <div className="p-col-12">
            <Steps model={items} activeIndex={activeIndex} readOnly={true} />
          </div>
        </div>

        <div className="p-mt-8 mt-4">{renderStepContent()}</div>
      </div>
    </AddPropertyContext.Provider>
  );
};

export default AddProperty;
